import React, { useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Pagination,
    Badge,
    Tooltip,
    CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "../../context/AuthContext";
import { useCompanyLiveViewSessionsQuery } from "../../features/systems/queries";

interface CompanyLiveViewSessionsProps {
    companyId: number;
    region: string;
}

export default function CompanyLiveViewSessions({ companyId, region }: CompanyLiveViewSessionsProps) {
    const { headersWithAuth } = useAuth();
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);

    const { data, isLoading, isError, error, refetch } = useCompanyLiveViewSessionsQuery(
        companyId,
        page,
        headersWithAuth
    );

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleClearSearch = () => {
        setSearchText("");
    };

    const handleRefresh = () => {
        refetch();
    };

    const formatDateTime = (dateTimeString: string | undefined) => {
        if (!dateTimeString) return "-";
        try {
            const date = new Date(dateTimeString);
            return date.toLocaleString();
        } catch (e) {
            return dateTimeString || "-";
        }
    };

    const formatDuration = (seconds: number) => {
        if (seconds < 60) return `${seconds}s`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    const sessions = data?.sessions || [];
    const totalPages = data?.numberOfPages || 1;
    const activeSessions = sessions.filter((session) => session.status === "ACTIVE").length;

    const filteredSessions = sessions.filter((session) => {
        if (!searchText.trim()) return true;
        const searchLower = searchText.toLowerCase().trim();
        return (
            (session.userEmail && session.userEmail.toLowerCase().includes(searchLower)) ||
            (session.systemName && session.systemName.toLowerCase().includes(searchLower)) ||
            (session.userId && session.userId.toLowerCase().includes(searchLower)) ||
            (session.systemId && session.systemId.toLowerCase().includes(searchLower))
        );
    });

    return (
        <Box sx={{ width: "100%", my: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" component="div">
                    Live View Sessions
                    {activeSessions > 0 && (
                        <Badge color="primary" badgeContent={activeSessions} sx={{ ml: 2 }}>
                            <Typography variant="body2" color="primary">
                                Active
                            </Typography>
                        </Badge>
                    )}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                        placeholder="Search sessions..."
                        size="small"
                        variant="outlined"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchText && (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="clear search"
                                        onClick={handleClearSearch}
                                        edge="end"
                                        size="small"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ width: 250 }}
                    />

                    <Tooltip title="Refresh sessions">
                        <IconButton onClick={handleRefresh} color="primary" size="small" aria-label="refresh sessions">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : isError ? (
                <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography color="error">
                        {error instanceof Error ? error.message : "Failed to load Live View sessions"}
                    </Typography>
                    <Button variant="outlined" color="primary" onClick={handleRefresh} sx={{ mt: 2 }}>
                        Retry
                    </Button>
                </Box>
            ) : filteredSessions.length === 0 ? (
                <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography>
                        {searchText ? `No sessions found matching "${searchText}"` : "No Live View sessions found."}
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>System</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Started At</TableCell>
                                    <TableCell>Last Activity</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSessions.map((session, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Tooltip title={`System ID: ${session.systemId}`}>
                                                <Typography>{session.systemName || session.systemId}</Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={`User ID: ${session.userId}`}>
                                                <Typography>{session.userEmail || session.userId}</Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{formatDateTime(session.startedAt)}</TableCell>
                                        <TableCell>{formatDateTime(session.lastUpdate)}</TableCell>
                                        <TableCell>{formatDuration(session.durationSeconds)}</TableCell>
                                        <TableCell>
                                            <Typography
                                                color={session.status === "ACTIVE" ? "primary" : "text.secondary"}
                                                fontWeight={session.status === "ACTIVE" ? "bold" : "normal"}
                                            >
                                                {session.status}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            {searchText.trim() ? (
                                <span>
                                    Showing {filteredSessions.length} results for <strong>"{searchText.trim()}"</strong>
                                </span>
                            ) : (
                                <span>Showing {filteredSessions.length} sessions</span>
                            )}
                        </Typography>

                        {totalPages > 1 && (
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                size="small"
                            />
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
}
